import { Injectable, effect, inject, untracked } from '@angular/core';
import { McGeoFeatureCollection } from '../data/geojson.models';
import {
    McLocationQuery,
    createMcEventNames,
    GeoTypesSchema,
    McQuerySchema,
} from './iq-mc-event-bus-state.models';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import {
    IqEventBusQueueService,
    IqEventBusBrokerService,
} from '@dat/iq-event-bus';
import { z } from 'zod';

const threeDigitZip = import('../data/three-digit-zip.data');
const market = import('../data/market-area.data');
const extendedMarket = import('../data/extended-market-area.data');
const region = import('../data/region.data');
const state = import('../data/state.data');
const country = import('../data/country.data');

type FeatureCollectionResponse = {
    featureCollection?: McGeoFeatureCollection;
    fetchInMs?: number;
    loadedInMs?: number;
    durationInMs?: number;
    loaded?: boolean;
};

type State = {
    queries: Record<string, McLocationQuery>;
    countries: FeatureCollectionResponse;
    extendedMarketAreas: FeatureCollectionResponse;
    marketAreas: FeatureCollectionResponse;
    regions: FeatureCollectionResponse;
    states: FeatureCollectionResponse;
    threeDigitZips: FeatureCollectionResponse;
};

const AreaPayloadSchema = z.object({
    query: McQuerySchema,
});

type AreaPayload = z.infer<typeof AreaPayloadSchema>;

@Injectable({
    providedIn: 'root',
})
export class IqMcEventBusStateAreaService extends ComponentStore<State> {
    /**
     * This is a service to queud the areas loader. The reason to use this is
     * so that it will make it easier for us to preload the areas and then emit as events
     * come in.
     */
    private _queue: IqEventBusQueueService = new IqEventBusQueueService();

    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);

    private _fetchKey: string = 'Area';

    constructor() {
        super({
            queries: {},
            countries: { featureCollection: null, loaded: false },
            extendedMarketAreas: { featureCollection: null, loaded: false },
            marketAreas: { featureCollection: null, loaded: false },
            regions: { featureCollection: null, loaded: false },
            states: { featureCollection: null, loaded: false },
            threeDigitZips: { featureCollection: null, loaded: false },
        });

        /**
         * This is a debug message to log the state of the area service
         */
        // effect(() => {
        //     console.log('MC Area State');
        //     console.log(this.state());
        // });

        //#region Handling the queue
        effect(() => {
            /**
             * If the user is not loaded, we will wait until it is loaded
             * so that all otel traces are pushed out.
             */
            if (!this.allAreasLoaded()) return;

            const unhandled = this._queue.unhandledEventNodeMap();

            if (this._queue.isAllHandled()) return;

            //#region JS Error Process

            /**
             * Gets all the JS errors that haven't been handled
             */
            const areaCalls = unhandled.get('AREA');

            untracked(() => {
                /**
                 * Iterates through the JS errors and pushes them to the otel
                 */
                areaCalls?.forEach((event) => {
                    const props = event.evt.payload as AreaPayload;
                    const area = this.getArea(props.query.geoType)();
                    const query = props.query;

                    /**
                     * Debugging Message
                     */
                    // console.log({
                    //     name: createMcEventNames(this._fetchKey, ['data']),
                    //     payload: {
                    //         query,
                    //         area,
                    //     },
                    // });

                    this._bus.dispatch({
                        name: createMcEventNames(this._fetchKey, ['data']),
                        payload: {
                            query,
                            area,
                        },
                    });
                    this._queue.handleEvent(event);
                });
            });
            //#endregion
        });
        //#endregion
    }

    allAreasLoaded = this.selectSignal<boolean>((state): boolean => {
        return (
            state?.countries.loaded &&
            state?.extendedMarketAreas.loaded &&
            state?.marketAreas.loaded &&
            state?.regions.loaded &&
            state?.states.loaded &&
            state?.threeDigitZips.loaded
        );
    });

    getArea = (area: string) =>
        this.selectSignal((state): FeatureCollectionResponse => {
            const areaKey = {
                MKT: 'marketAreas',
                X_MKT: 'extendedMarketAreas',
                COUNTRY: 'countries',
                REGION: 'regions',
                STATE: 'states',
                THREEDZ: 'threeDigitZips',
            }[area];

            return state[areaKey];
        });

    getMarket = (code: string, area: string) =>
        this.selectSignal(
            this.getArea(area),
            (area: FeatureCollectionResponse) =>
                area?.featureCollection?.features?.find(
                    (feature) => feature.properties.code === code,
                ),
        );

    intializeEventRegister(): void {
        this._bus.registerMap({
            /**
             * This is an event to log non critical events. This
             * can be used to keep track of non critical errors.
             */
            // type:mc-fetch:Area
            [createMcEventNames(this._fetchKey)]: {
                callback: (payload: AreaPayload) => {
                    try {
                        const checkoutPayload: AreaPayload =
                            AreaPayloadSchema.parse(payload);

                        this._queue.addEvent({
                            name: 'AREA',
                            payload: checkoutPayload,
                        });
                    } catch (error) {
                    }
                },
            },
        });
    }

    initialize(): Promise<void[]> {
        if (this.allAreasLoaded()) return Promise.resolve([]);

        const start = Date.now();

        const threeDzPromise = threeDigitZip.then((data) => {
            const loadedInMs = Date.now();

            this.patchState({
                threeDigitZips: {
                    featureCollection: data.default,
                    loaded: true,
                    loadedInMs,
                    fetchInMs: start,
                    durationInMs: loadedInMs - start,
                },
            });
        });
        const marketPromise = market.then((data) => {
            const loadedInMs = Date.now();

            this.patchState({
                marketAreas: {
                    featureCollection: data.default,
                    loaded: true,
                    loadedInMs,
                    fetchInMs: start,
                    durationInMs: loadedInMs - start,
                },
            });
        });
        const extendedMarketPromise = extendedMarket.then((data) => {
            const loadedInMs = Date.now();

            this.patchState({
                extendedMarketAreas: {
                    featureCollection: data.default,
                    loaded: true,
                    loadedInMs,
                    fetchInMs: start,
                    durationInMs: loadedInMs - start,
                },
            });
        });
        const statePromise = state.then((data) => {
            const loadedInMs = Date.now();

            this.patchState({
                states: {
                    featureCollection: data.default,
                    loaded: true,
                    loadedInMs,
                    fetchInMs: start,
                    durationInMs: loadedInMs - start,
                },
            });
        });
        const regionPromise = region.then((data) => {
            const loadedInMs = Date.now();

            this.patchState({
                regions: {
                    featureCollection: data.default,
                    loaded: true,
                    loadedInMs,
                    fetchInMs: start,
                    durationInMs: loadedInMs - start,
                },
            });
        });
        const countryPromise = country.then((data) => {
            const loadedInMs = Date.now();

            this.patchState({
                countries: {
                    featureCollection: data.default,
                    loaded: true,
                    loadedInMs,
                    fetchInMs: start,
                    durationInMs: loadedInMs - start,
                },
            });
        });

        return Promise.all([
            threeDzPromise,
            marketPromise,
            extendedMarketPromise,
            statePromise,
            regionPromise,
            countryPromise,
        ]);
    }
}
